/**
 * Phase-1 realtime collaborative editor.
 *
 * TipTap + Yjs + y-websocket. Every keystroke is a CRDT op; the server
 * fans it out to other connected clients. No save button, no Tier-2
 * conflict, no race window. Convergence is mathematical.
 *
 * Important React lifecycle note:
 *   The Y.Doc and the WebsocketProvider must exist BEFORE useEditor
 *   evaluates its extensions list — `CollaborationCursor.configure({
 *   provider })` reads `provider.awareness` synchronously at editor
 *   construction. So both are built in a single `useMemo`, not in a
 *   useEffect. Cleanup runs in a separate useEffect tied to the same
 *   dependency keys.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import Collaboration from '@tiptap/extension-collaboration'
// TipTap v3 renamed the live-cursor extension from "collaboration-cursor"
// to "collaboration-caret". Deaktiviert 2026-05-19 wegen "Method
// unimplemented"/"_first undefined" beim Server-XmlFragment; mit
// collaboration-caret 3.23 + y-tiptap 3.0.3 am 2026-09-30 in zwei
// Headless-Browsern gegen den Live-Server geprueft: kein Fehler, Carets
// beider Seiten sichtbar. Wieder aktiv (Alex: "Cursor der Menschen").
import CollaborationCaret from '@tiptap/extension-collaboration-caret'
// V2 Authorship — Block-Level Edition.
// Server stamps `author_id`, `author_name`, `author_color` on paragraph
// XmlElement.attributes via pycrdt; ParagraphWithAuthor declares those
// attrs in TipTap schema so y-tiptap maps them in; AuthorHighlight
// reads them for the per-paragraph color bar. No marks involved →
// no mark-merge race conditions (which is what wiped agent inserts
// in the V2.0 Mark-Level attempt).
import { ParagraphWithAuthor } from './ParagraphWithAuthor'
import { FieldSalienceDecoration } from './FieldSalienceDecoration'
import { useApplyFieldSalience } from '../hooks/useApplyFieldSalience'
import { AuthorHighlight, collectAuthorsInDoc } from './AuthorHighlight'
import { WordLevelAuthorship } from './WordLevelAuthorship'
import { PendingDraftDecoration } from './PendingDraftDecoration'
import { MarkdownSource, setMarkdownSource } from './MarkdownSource'
import { AnnotationOverlay } from './AnnotationOverlay'
import { InlineBlockMenu } from './InlineBlockMenu'
import { QuestionConversionHint } from './QuestionConversionHint'
import { annotationsFromSections } from './annotationsFromSections'
import { structuredFromEditor } from './structuredFromEditor'
import {
  AnnotationHighlight,
  setAnnotations,
  type AnnotationData,
} from './AnnotationHighlight'
import { AnnotationTooltip } from './AnnotationTooltip'
import { refreshAccessToken, getAccessToken } from '../auth'
import Mention from '@tiptap/extension-mention'
import { makeMentionSuggestion, type MentionItem } from './mentionSuggestion'
import { colorFromId as sharedColorFromId } from './colorFromId'
import * as Y from 'yjs'
import { WebsocketProvider } from 'y-websocket'
import { uploadForPost } from '../api/postMedia'
import { authFetch } from '../api/authFetch'

import { CONTENT_API, CONTENT_WS } from '../config'

interface Props {
  postId: number
  /** JWT for the WS handshake. Should already be a valid bearer token. */
  token: string | null
  /** Identity info used for awareness — shown to other connected clients. */
  user: {
    id: string
    name: string
    /** CSS color string for this user's cursor + label. */
    color: string
  }
  /** Optional className for the editor container. */
  className?: string
  /** Optional placeholder when the doc is empty. */
  placeholder?: string
  /** Read-only mode — still shows live updates and remote cursors, but
   *  this client can't type. Used for the "Read" view-mode tab. */
  readOnly?: boolean
  /** Names available for @-mention autocomplete — typically the post's
   *  `metadata_json.notify_authors`. Falls back to an empty dropdown
   *  when absent. */
  mentionItems?: string[]
  /** Receive a stable reference to the TipTap Editor instance after
   *  mount. Used by canvas-mode to scope the editor to a single
   *  paragraph for in-place card edit (Option-4 hybrid pattern per
   *  Content IACP c625d7f9). */
  onEditorReady?: (editor: import('@tiptap/react').Editor | null) => void
  /** Bilder beim Einfügen privat hochladen und an den Beitrag binden. */
  privateUploads?: boolean
}

export type CollabStatus = 'connecting' | 'live' | 'offline'

/**
 * Cheap JWT exp-claim decode — no signature check, just look at the
 * payload. Returns true if the token is past `exp` or within 30s of it
 * (so we trigger refresh slightly early to avoid races).
 */
function isJwtNearExpiry(token: string): boolean {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return true
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')))
    const exp = typeof payload.exp === 'number' ? payload.exp : 0
    const now = Math.floor(Date.now() / 1000)
    return !exp || exp - now < 30
  } catch {
    return true
  }
}

// Re-export the shared colorFromId so existing callers
// (`import { colorFromId } from '../components/CollabEditor'`) keep
// working. The actual implementation lives in `./colorFromId.ts` —
// kept in a dedicated module so AuthorHighlight, CollabReadView and
// others can pull it in without a circular import via CollabEditor.
const colorFromId = sharedColorFromId

export function CollabEditor({ postId, token, user, className, placeholder, readOnly = false, mentionItems, onEditorReady, privateUploads = false }: Props) {
  const [status, setStatus] = useState<CollabStatus>('connecting')
  const [peers, setPeers] = useState<Array<{ id: string; name: string; color: string; writing: boolean }>>([])
  // Markdown-Quellmodus (#2242): Zeichen sichtbar, aber formatiert. Vorgabe
  // an; die Wahl bleibt im Browser.
  const [mdSource, setMdSource] = useState<boolean>(() => {
    try { return localStorage.getItem('content_md_source') !== '0' } catch { return true }
  })
  // All authors discovered in the doc via AuthorMark — used for the
  // legend strip above the editor. Updated on every transaction so it
  // reflects fresh remote writes (e.g. an agent appending a block).
  const [docAuthors, setDocAuthors] = useState<
    Array<{ id: string; name: string; color: string }>
  >([])

  // Build doc + provider synchronously — they must exist when useEditor
  // wires up Collaboration + CollaborationCursor below.
  //
  // Defensive token check: a stale token from localStorage would 403
  // every WS attempt in a loop. We decode the JWT's `exp` claim and
  // kick off a refresh BEFORE the first connect if it's already past
  // expiry (or within 30s of it). Async — the WS will retry within
  // y-websocket's retry-backoff so the fresh token gets picked up.
  const { ydoc, provider } = useMemo(() => {
    const doc = new Y.Doc()
    const baseUrl = `${CONTENT_WS}/api/v1/ws/posts/${postId}`
    const params: Record<string, string> = {}
    let initialToken = getAccessToken() || token
    if (initialToken && isJwtNearExpiry(initialToken)) {
      // Kick off refresh; first connect will likely 403, then
      // connection-close handler (or y-websocket's internal retry)
      // will pick up the freshly cached token on the next attempt.
      void refreshAccessToken().then(() => {
        const fresh = getAccessToken()
        if (fresh && fresh !== initialToken) {
          const opts = (ws as unknown as {
            params: Record<string, string>
            protocols: string[]
          })
          if (opts.params) opts.params.token = fresh
          if (Array.isArray(opts.protocols)) {
            opts.protocols.length = 0
            opts.protocols.push(`bearer.${fresh}`)
          }
        }
      })
    }
    if (initialToken) params.token = initialToken
    const ws = new WebsocketProvider(baseUrl, 'crdt', doc, {
      connect: true,
      params,
      protocols: initialToken ? [`bearer.${initialToken}`] : [],
    } as unknown as ConstructorParameters<typeof WebsocketProvider>[3])

    // Bridge: when the underlying WS closes with an auth-error code
    // (4xxx in the y-websocket convention, or HTTP-equivalent 401/403
    // bubbled as 1008 policy-violation), refresh the token before
    // y-websocket retries. y-websocket's reconnect uses the same
    // params dict it was constructed with, so we mutate it in place
    // so the next attempt carries the fresh token.
    ws.on(
      'connection-close',
      async (event: CloseEvent | null) => {
        const code = event?.code ?? 0
        const isAuthError = code === 1008 || code === 4401 || code === 4403
        if (!isAuthError) return
        const ok = await refreshAccessToken()
        if (!ok) return
        const fresh = getAccessToken()
        if (fresh) {
          // Mutate params + protocols in place — y-websocket reuses
          // these on retry.
          const opts = (ws as unknown as {
            params: Record<string, string>
            protocols: string[]
          })
          if (opts.params) opts.params.token = fresh
          if (Array.isArray(opts.protocols)) {
            opts.protocols.length = 0
            opts.protocols.push(`bearer.${fresh}`)
          }
        }
      }
    )

    return { ydoc: doc, provider: ws }
  }, [postId, token])

  // Side-effects against the provider (status + awareness listeners,
  // cleanup on unmount or postId change).
  useEffect(() => {
    const onStatus = ({ status: s }: { status: 'connected' | 'connecting' | 'disconnected' }) => {
      setStatus(
        s === 'connected' ? 'live'
          : s === 'connecting' ? 'connecting'
          : 'offline'
      )
    }
    provider.on('status', onStatus)

    provider.awareness.setLocalStateField('user', {
      id: user.id,
      name: user.name,
      color: user.color,
    })

    const onAwareness = () => {
      const states = Array.from(provider.awareness.getStates().entries())
      // Eine Person kann mit zwei Verbindungen da sein (Lesemodus-Tab +
      // Popover-Editor) — nach id zusammenziehen, Schreibzustand gewinnt.
      const byId = new Map<string, { id: string; name: string; color: string; writing: boolean }>()
      for (const [, s] of states) {
        const u = (s as { user?: { id?: string; name?: string; color?: string; mode?: string } }).user
        if (!u || !u.id || !u.name) continue
        if (u.id === user.id) continue
        const writing = u.mode !== 'read'
        const prev = byId.get(u.id)
        if (!prev || (!prev.writing && writing)) {
          byId.set(u.id, { id: u.id, name: u.name, color: u.color || colorFromId(u.id), writing })
        }
      }
      setPeers(Array.from(byId.values()))
    }
    provider.awareness.on('update', onAwareness)
    onAwareness()

    return () => {
      provider.off('status', onStatus)
      provider.awareness.off('update', onAwareness)
      provider.destroy()
      ydoc.destroy()
    }
  }, [provider, ydoc, user.id, user.name, user.color])

  // Image-insert helper used by the editorProps drop + paste handlers.
  // Mirrors FieldFront's canvas-mode dropImageOnCard (Pattern owned by
  // CanvasView.tsx:574) but parameterised on `postId` + paragraph_index
  // resolved from ProseMirror's view at drop/paste time. Uploads file
  // to storage-api (permanent + public), wraps the URL in image-
  // markdown, posts as a new paragraph with explicit block_type='media'
  // via REST /crdt/apply. Errors logged but not surfaced — Phase 5d
  // can add a toast.
  // Die Editor-Handler werden einmal gebunden; der Schalter kann sich
  // danach ändern, deshalb über einen Ref lesen.
  const privateUploadsRef = useRef(privateUploads)
  useEffect(() => { privateUploadsRef.current = privateUploads }, [privateUploads])

  const handleImageInsert = async (
    targetPostId: number,
    file: File,
    paragraphIndex: number,
  ): Promise<void> => {
    try {
      const { url } = await uploadForPost(file, {
        postId: targetPostId,
        context: `post-${targetPostId}-paragraph-${paragraphIndex}`,
        // Schalter „Bilder privat" in der Werkzeugleiste (#5235).
        isPrivate: privateUploadsRef.current,
      })
      const altText = file.name.replace(/\.[^.]+$/, '') || 'image'
      const imageMarkdown = `![${altText}](${url})`
      await authFetch(
        `${CONTENT_API}/api/v1/posts/${targetPostId}/crdt/apply`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            mode: 'insert_after_paragraph',
            paragraph_index: paragraphIndex,
            text: imageMarkdown,
            block_type: 'media',
            // P0.5 (Sprint D, Codex t-c3a791503c16): defer the push
            // so the image lands silently. The next text-commit (Enter
            // / blur with caption) fires the push and recipients see
            // image + caption together. Prevents Confabulation-light
            // when bots receive an image without context.
            suppress_push: true,
          }),
        },
      )
    } catch (err) {
      console.error('handleImageInsert failed', err)
    }
  }

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // In TipTap v3 the local history extension is `undoRedo` (the old
        // `history` key is silently ignored). Yjs's Collaboration extension
        // owns undo/redo on its own.
        undoRedo: false,
        // Disable StarterKit's default Paragraph so our extended
        // ParagraphWithAuthor wins — same node name, schema-compatible,
        // but with author_id/author_name/author_color attrs added.
        paragraph: false,
        // Links bleiben Markdown-Text; eine Link-Mark ginge beim
        // Materialisieren auf dem Server verloren.
        link: { autolink: false, linkOnPaste: false, openOnClick: false },
      }),
      // Markdown-Quellmodus: Decorations plus Tastenkürzel, die Markdown-
      // Zeichen einfügen statt Marks (#2242).
      MarkdownSource.configure({ enabled: mdSource }),
      ParagraphWithAuthor.configure({
        user: {
          id: user.id,
          name: user.name,
          color: user.color,
        },
      }),
      Placeholder.configure({
        placeholder: placeholder || 'Tippe los — die Aenderungen werden live geteilt …',
      }),
      Collaboration.configure({
        document: ydoc,
        // Same field name the backend's snapshot picks up
        // (content/services/crdt_service.py:CRDTSession.text = doc.get("content")).
        field: 'content',
      }),
      CollaborationCaret.configure({
        provider,
        user: { id: user.id, name: user.name, color: user.color },
      }),
      AuthorHighlight.configure({
        awareness: provider.awareness,
      }),
      // V2.3 Word-Level Authorship (Task #166) — inline tint per
      // authorship-run sidecar (server-stamped via V2.3 P2). Subtle
      // 12% alpha background for refined spans; no-op on uniform
      // paragraphs so the border-bar from AuthorHighlight stays clean.
      WordLevelAuthorship.configure({
        alpha: 0.12,
        enabled: true,
      }),
      // V2.7.13: inline annotation display — yellow underline + hover
      // tooltip on text spans where someone left a comment via the
      // selection-based AnnotationOverlay. Re-anchors snippets by
      // string-match to survive CRDT char-position drift.
      AnnotationHighlight,
      // Visual feedback: freshly-typed text appears faded grey while
      // it's still "pending commit" (no trigger has fired yet). After
      // 3 sec of quiet — same heuristic the server uses for
      // newline_pause / inactivity — the text matures to normal color.
      // Tells the user "your write is buffered, it'll go out shortly".
      PendingDraftDecoration,
      // Phase 2.5: anti-Echo cues from field_map subscribed via
      // useApplyFieldSalience below. The extension hosts the
      // Decoration plugin; React effect pushes data on each poll.
      FieldSalienceDecoration,
      Mention.configure({
        // Render as plain `@AgentName` text + a styled span. Keeps the
        // server-side mention_parser happy (it looks for `@<Name>` in
        // the materialized doc text, not a DOM tag).
        HTMLAttributes: { class: 'mention-chip' },
        renderText({ node }) {
          return `@${node.attrs.label || node.attrs.id}`
        },
        deleteTriggerWithBackspace: true,
        suggestion: makeMentionSuggestion((query: string) => {
          const list: MentionItem[] = (mentionItems || []).map((name) => ({
            id: name,
            name,
            color: colorFromId(`agent:${name}`),
          }))
          if (!query) return list
          const q = query.toLowerCase()
          return list.filter((item) => item.name.toLowerCase().includes(q))
        }),
      }),
    ],
    immediatelyRender: false,
    editable: !readOnly,
    // Keine Input- und Paste-Rules: `## ` darf kein Heading-Knoten werden,
    // `**x**` keine Bold-Mark. Der Server kennt nur den Text.
    enableInputRules: false,
    enablePasteRules: false,
    editorProps: {
      // Image drag-and-drop + paste in Doc-mode. Mirrors FieldFront's
      // canvas-mode dropImageOnCard (CanvasView.tsx:574) but anchored to
      // ProseMirror's view: posAtCoords gives us the paragraph under the
      // cursor at drop-time; for paste we use the current selection
      // anchor.
      //
      // dragover/dragenter: ProseMirror's default `handleDOMEvents` no
      // longer auto-cancels external-file drags in TipTap v3+, so we
      // explicitly preventDefault here. Without this, the Browser
      // rejects the drop on the editor surface and shows the "not
      // allowed" cursor — `handleDrop` then never fires because the
      // drop event was never produced. Alex 2026-05-28 regression.
      handleDOMEvents: {
        dragover: (_view, event) => {
          const dt = (event as DragEvent).dataTransfer
          if (!dt) return false
          // Only intercept when the drag carries files (an image, a
          // PDF, anything from the OS). Pure-text drags inside the
          // doc keep their normal behaviour.
          const hasFiles = dt.types?.includes?.('Files') ?? false
          if (!hasFiles) return false
          event.preventDefault()
          dt.dropEffect = 'copy'
          return false  // let other handlers still see it
        },
        dragenter: (_view, event) => {
          const dt = (event as DragEvent).dataTransfer
          if (!dt) return false
          const hasFiles = dt.types?.includes?.('Files') ?? false
          if (!hasFiles) return false
          event.preventDefault()
          return false
        },
      },
      handleDrop: (view, event, _slice, _moved) => {
        if (readOnly) return false
        const dt = event.dataTransfer
        if (!dt || !dt.files || dt.files.length === 0) return false
        const file = dt.files[0]
        if (!file || !file.type.startsWith('image/')) return false
        event.preventDefault()
        const coords = { left: event.clientX, top: event.clientY }
        const pos = view.posAtCoords(coords)?.pos ?? view.state.selection.from
        const $pos = view.state.doc.resolve(pos)
        // Find the paragraph_index by walking ancestors. Top-level
        // children of the doc are paragraphs (in our schema); the
        // index in that list is paragraph_index for the REST call.
        let paragraphIndex = $pos.index(0)
        // Clamp to valid range (in case posAtCoords lands past end).
        const total = view.state.doc.childCount
        if (paragraphIndex < 0) paragraphIndex = 0
        if (paragraphIndex >= total) paragraphIndex = total - 1
        void handleImageInsert(postId, file, paragraphIndex)
        return true
      },
      handlePaste: (view, event, _slice) => {
        if (readOnly) return false
        const cd = event.clipboardData
        if (!cd) return false
        // First image item wins. Paste of a screenshot from clipboard
        // surfaces as an `image/*`-typed DataTransferItem; text-only
        // pastes leave the default ProseMirror behaviour intact.
        const items = Array.from(cd.items || [])
        const imgItem = items.find(
          (it) => it.kind === 'file' && it.type.startsWith('image/'),
        )
        if (!imgItem) return false
        const file = imgItem.getAsFile()
        if (!file) return false
        event.preventDefault()
        const pos = view.state.selection.from
        const $pos = view.state.doc.resolve(pos)
        let paragraphIndex = $pos.index(0)
        const total = view.state.doc.childCount
        if (paragraphIndex < 0) paragraphIndex = 0
        if (paragraphIndex >= total) paragraphIndex = total - 1
        void handleImageInsert(postId, file, paragraphIndex)
        return true
      },
    },
    // Emit a `blur` intent via awareness when the editor loses focus.
    // Server-side `CRDTSession.handle_awareness_update` picks this up
    // and fires a `chunk_committed` event with trigger='blur' to all
    // subscribed agents — the strongest commit-intent signal we have.
    // Wire shape matches the server decoder: flat fields on the local
    // awareness state, dedup-keyed by (clientID, ts).
    onBlur: ({ editor: ed }) => {
      try {
        const aw = provider?.awareness
        if (aw) {
          aw.setLocalStateField('intent', 'blur')
          aw.setLocalStateField('ts', Date.now())
        }
      } catch {
        // awareness may not be ready during teardown — ignore
      }
      // Clear pending-draft visual state — the server's blur trigger
      // will fire its commit, so the local user should see the text
      // mature to normal color immediately.
      try {
        const tr = ed.state.tr.setMeta('pendingDraftDecoration:clear', true)
        ed.view.dispatch(tr)
      } catch {
        /* ignore */
      }
    },
    // Clear the blur intent when the user comes back, so a later
    // reconnect-storm doesn't re-emit a stale blur (server has its
    // own dedup but this keeps the wire clean).
    onFocus: () => {
      try {
        const aw = provider?.awareness
        if (!aw) return
        aw.setLocalStateField('intent', null)
        aw.setLocalStateField('ts', null)
      } catch {
        /* ignore */
      }
    },
  }, [ydoc, provider, readOnly])

  // Phase 2.5: stream field_map salience snapshots into the editor's
  // FieldSalienceDecoration plugin. Polls every ~7s while mounted;
  // each tick repushes tension + dominated paragraph index sets.
  useApplyFieldSalience({ editor, postId, enabled: !readOnly })

  useEffect(() => {
    if (editor) setMarkdownSource(editor, mdSource)
    try { localStorage.setItem('content_md_source', mdSource ? '1' : '0') } catch { /* privat */ }
  }, [editor, mdSource])

  // Propagate the editor instance to the parent for canvas-mode
  // popover use (Option-4 hybrid pattern). Only fires once per editor
  // instance — parent gets a stable reference.
  useEffect(() => {
    if (!editor || !onEditorReady) return
    onEditorReady(editor)
    // Beim Abbau die Referenz zurücknehmen, damit niemand auf einem
    // zerstörten Editor Befehle ausführt.
    return () => onEditorReady(null)
  }, [editor, onEditorReady])

  // Refresh the authors-legend whenever the doc changes —
  // collectAuthorsInDoc walks the PM tree and dedupes by author_id,
  // returning {id, name, color} per distinct author.
  useEffect(() => {
    if (!editor) return
    const refresh = () => {
      const authors = collectAuthorsInDoc(
        editor.state.doc as unknown as Parameters<typeof collectAuthorsInDoc>[0]
      )
      setDocAuthors(authors)
    }
    refresh()
    const onUpdate = () => refresh()
    editor.on('update', onUpdate)
    return () => {
      editor.off('update', onUpdate)
    }
  }, [editor])

  // Issue #15: poll the annotations endpoint and push the list both
  // into the AnnotationHighlight extension (PM decorations) AND into
  // React state for the AnnotationTooltip overlay. The annotation
  // response carries start_pos/end_pos in char-coords against the
  // server-side materialized text; AnnotationHighlight uses those
  // as the authoritative anchor + the snippet as a CRDT-drift fallback.
  const [annotations, setAnnotationsState] = useState<AnnotationData[]>([])
  useEffect(() => {
    if (!editor) return
    let cancelled = false
    // Push-only path (2026-05-29): section-sourced annotations are
    // derived directly from the live editor doc on every TipTap update
    // — no HTTP poll needed because the Yjs WebSocket has already
    // delivered the change. We only fetch /annotations (legacy DB rows)
    // periodically as a low-frequency heartbeat; once the migration
    // has run, legacy rows are migrated and we can drop this entirely.
    let legacyDbAnnotations: AnnotationData[] = []
    const recompute = () => {
      const sectionAnnotations = annotationsFromSections(
        structuredFromEditor(editor),
      )
      const merged = [...legacyDbAnnotations, ...sectionAnnotations]
      setAnnotations(editor.view, merged)
      if (!cancelled) setAnnotationsState(merged)
    }
    const fetchLegacyDb = async () => {
      const tok = getAccessToken()
      if (!tok) return
      try {
        const annRes = await fetch(
          `${CONTENT_API}/api/v1/posts/${postId}/annotations/`,
          { headers: { Authorization: `Bearer ${tok}` } },
        )
        if (cancelled || !annRes.ok) return
        const annList = (await annRes.json()) as Array<{
          id: number
          start_pos: number
          end_pos: number
          note_text: string
          author_id?: string | null
          author_name: string
          created_at?: string | null
        }>
        const serverText = editor.state.doc.textBetween(
          0,
          editor.state.doc.content.size,
          '\n',
        )
        legacyDbAnnotations = annList
          .filter((a) => !(a.note_text || '').startsWith('[MIGRATED:'))
          .map((a) => {
            let snippet = ''
            try {
              const start = Math.max(0, a.start_pos | 0)
              const end = Math.min(serverText.length, a.end_pos | 0)
              if (end > start) snippet = serverText.slice(start, end)
            } catch {
              /* ignore */
            }
            return {
              id: a.id,
              start_pos: a.start_pos,
              end_pos: a.end_pos,
              note_text: a.note_text,
              author_id: a.author_id ?? null,
              author_name: a.author_name,
              created_at: a.created_at ?? null,
              snippet,
              source: 'db' as const,
            }
          })
        recompute()
      } catch {
        /* swallow */
      }
    }
    // Wire the live-derive: every doc update (local edit OR incoming
    // y-sync from another agent) triggers a recompute. This replaces
    // the old 10 s /crdt/structured + /crdt/text polls.
    const onUpdate = () => recompute()
    editor.on('update', onUpdate)
    // Initial pass + one legacy fetch on mount; a 60 s heartbeat for
    // DB rows added externally (e.g. from the legacy API).
    recompute()
    void fetchLegacyDb()
    const id = window.setInterval(fetchLegacyDb, 60_000)
    return () => {
      cancelled = true
      editor.off('update', onUpdate)
      window.clearInterval(id)
    }
  }, [editor, postId])

  // Issue #15: DOM root for AnnotationTooltip's delegated hover.
  // The TipTap editor view's dom is the ProseMirror surface where
  // [data-annotation-id] spans live. Captured into local state so the
  // tooltip remounts when the editor is recreated.
  const [editorRoot, setEditorRoot] = useState<HTMLElement | null>(null)
  useEffect(() => {
    setEditorRoot((editor?.view.dom as HTMLElement | undefined) ?? null)
  }, [editor])

  const statusLabel = status === 'live' ? (readOnly ? '● live (read-only)' : '● live')
    : status === 'connecting' ? '◌ connecting'
    : '○ offline'

  return (
    <div className={`collab-editor${mdSource ? ' md-source' : ''} ${className || ''}`}>
      <div className="collab-editor-statusbar">
        <span className={`collab-status collab-status--${status}`}>{statusLabel}</span>
        <button
          type="button"
          className={`collab-md-toggle${mdSource ? ' is-on' : ''}`}
          onClick={() => setMdSource((v) => !v)}
          title={mdSource ? 'Markdown-Zeichen sichtbar und formatiert. Klick: nur Text.' : 'Markdown-Zeichen formatiert anzeigen'}
          aria-pressed={mdSource}
        >
          <i className="fab fa-markdown" /> Markdown
        </button>
        {peers.length > 0 && (
          <span className="collab-peers">
            {peers.map(p => (
              <span
                key={p.id}
                className="collab-peer"
                title={p.name}
                style={{ backgroundColor: p.color }}
              >
                {p.name.charAt(0).toUpperCase()}
              </span>
            ))}
            <span className="collab-peers-label">
              {(() => {
                const writers = peers.filter((p) => p.writing).length
                const readers = peers.length - writers
                const parts: string[] = []
                if (writers > 0) parts.push(writers === 1 ? 'tippt mit dir' : `${writers} tippen mit dir`)
                if (readers > 0) parts.push(readers === 1 ? 'liest mit' : `${readers} lesen mit`)
                return parts.join(' · ')
              })()}
            </span>
          </span>
        )}
      </div>
      {docAuthors.length > 0 && (
        <div className="collab-authors-legend">
          <span className="collab-authors-label">Autoren:</span>
          {docAuthors.map(a => (
            <span key={a.id} className="collab-author-chip" title={a.name}>
              <span
                className="collab-author-swatch"
                style={{ backgroundColor: a.color }}
              />
              <span className="collab-author-name">{a.name}</span>
            </span>
          ))}
        </div>
      )}
      <EditorContent editor={editor} className="collab-editor-surface" />
      {/* Issue #15: styled hover tooltip for annotation highlights.
          Delegates mouseover/mouseout on the editor's PM root, looks
          up annotations by id, renders portal-mounted designed tooltip
          (replaces native browser `title`). */}
      <AnnotationTooltip editorRoot={editorRoot} annotations={annotations} />
      {/* Selection-based annotation overlay (V2.7.13): renders a
          floating "💬 Anmerken" button when the user has text selected;
          a modal dialog when the button is clicked. Submit POSTs to
          the annotations endpoint, which fires a chunk_committed push
          with trigger='annotation' to notify_authors. */}
      {!readOnly && editor && (
        <AnnotationOverlay
          editor={editor}
          postId={postId}
          onCreated={() => {
            // Trigger immediate refetch so the new annotation appears
            // as a yellow highlight without waiting for the 10s poll.
            // We re-render the parent by bumping a state — cheapest
            // way is to dispatch a no-op meta tx; the next polling
            // tick will pick it up. For now we rely on the interval.
          }}
        />
      )}
      {/* Phase B inline-UX (Alex 2026-05-30): the "+" handle on empty
          paragraphs + floating menu for inserting a Frage / Aufgabe /
          Product section at the cursor position. Replaces the old
          TopBar Section-button + modal flow. */}
      {!readOnly && editor && (
        <InlineBlockMenu editor={editor} postId={postId} />
      )}
      {/* P0.1 (Sprint B): detect plaintext-with-? + ≥2 notify_authors
          and offer auto-conversion to a Question-Section so the writer
          can pick explicit `targets` instead of broadcasting to all
          notify_authors. Recycles A.1 routing — no new server surface. */}
      {!readOnly && editor && token && (mentionItems?.length ?? 0) >= 2 && (
        <QuestionConversionHint
          editor={editor}
          postId={postId}
          token={token}
          notifyAuthors={mentionItems || []}
          initiatorName={user.name}
        />
      )}
    </div>
  )
}

export { colorFromId }
